import React, { useState, useEffect, useCallback, useRef, useId } from 'react';
import { AnimatePresence } from 'motion/react';
import { X, CaretLeft, CaretRight, Globe, MapPin, Phone, FolderPlus } from '@phosphor-icons/react';
import api from '../services/api';
import { useScrollLock } from '../hooks/useScrollLock';
import CopyableId from './CopyableId';
import CollectionPickerModal from './CollectionPickerModal';
import ExplorationLinks from './ExplorationLinks';
import { getPlatformName } from '../utils/itemHelpers';
import Modal, { ENTITY_PANEL_MOTION } from './Modal';

function getHostname(url) {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return url;
  }
}

export default function PlaceDetailModal({
  place, onClose,
  onPrev, onNext, hasPrev = false, hasNext = false,
  itemIndex, totalItems,
  onOpenItem, onOpenPlace,
  // Pass-through to the shared <Modal>'s own focus-trap-react options — the
  // same escape hatch Modal itself exposes (see Modal.jsx), threaded here
  // purely so tests can pass jsdom's `displayCheck: 'none'` (jsdom can't
  // compute layout for focus-trap's real visibility check). Not used in
  // production.
  focusTrapOptions,
}) {
  const { targetRef: scrollRef, unlock: unlockScroll } = useScrollLock(true);

  const handleClose = useCallback(() => {
    unlockScroll();
    onClose();
  }, [unlockScroll, onClose]);

  const titleId = useId();
  const [siblings, setSiblings] = useState([]);
  const [sourceItemDetail, setSourceItemDetail] = useState(null);

  const {
    entity_name, place_data = {}, enrichments = [],
    creator_contexts = [], sources = [],
  } = place;
  const displayName = place_data?.display_name || entity_name;

  const primaryEnrichment = enrichments[0] || {};
  const { answer } = primaryEnrichment;

  const [showCollectionPicker, setShowCollectionPicker] = useState(false);
  const [collectionToast, setCollectionToast] = useState(null);
  const collectionToastRef = useRef(null);

  useEffect(() => {
    return () => {
      if (collectionToastRef.current) clearTimeout(collectionToastRef.current);
    };
  }, []);

  const firstSource = sources[0];

  // Fetch first source item detail for creator attribution
  useEffect(() => {
    if (!firstSource?.item_id) return;
    let cancelled = false;
    api.getItem(firstSource.item_id, { includeRawResponse: true })
      .then(data => {
        if (!cancelled) setSourceItemDetail(data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [firstSource?.item_id]);

  // Fetch sibling entities from the same analysis
  useEffect(() => {
    if (!primaryEnrichment.item_id) return;
    let cancelled = false;
    api.getEnrichments({ itemId: primaryEnrichment.item_id, status: 'complete', limit: 50 })
      .then(data => {
        if (cancelled) return;
        const enrichmentIds = new Set(enrichments.map(e => e.enrichment_id));
        const others = (data.enrichments || []).filter(e => !enrichmentIds.has(e.enrichment_id));
        setSiblings(others);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [primaryEnrichment.item_id, enrichments]);

  // Keyboard: arrows for prev/next. Escape-to-close is handled by the
  // shared <Modal> primitive.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft' && hasPrev && onPrev) { e.preventDefault(); onPrev(); }
      if (e.key === 'ArrowRight' && hasNext && onNext) { e.preventDefault(); onNext(); }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onPrev, onNext, hasPrev, hasNext]);

  // COL-103 (owner decision 2026-08-17): Google Places photos are no longer
  // requested, stored or rendered. The item screenshot is the only image.
  const displayImage = firstSource?.item_image;

  const mapsUrl = place_data?.google_maps_uri
    || (place_data?.formatted_address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place_data.formatted_address)}`
      : null);

  // Extract creator attribution from source item
  const mediaMetadata = sourceItemDetail?.latest_analysis?.raw_response?.media_metadata;
  const originalPoster = mediaMetadata?.original_poster;
  const likelySource = mediaMetadata?.likely_source;
  const sourceUrl = sourceItemDetail?.source_url;

  // Determine description: show editorial_summary as fallback when no creator content
  const hasCreatorContent = creator_contexts.length > 0;
  const descriptionText = hasCreatorContent
    ? null
    : (place_data?.editorial_summary || answer || null);

  return (
    <>
      <Modal
        onClose={handleClose}
        labelledBy={titleId}
        className="entity-detail-modal"
        panelMotionProps={ENTITY_PANEL_MOTION}
        focusTrapOptions={focusTrapOptions}
      >
        <button className="entity-detail-close" onClick={handleClose} aria-label="Close">
          <X size={20} />
        </button>

        {hasPrev && onPrev && (
          <button className="entity-detail-nav entity-detail-nav-prev" onClick={onPrev} aria-label="Previous place">
            <CaretLeft size={20} />
          </button>
        )}
        {hasNext && onNext && (
          <button className="entity-detail-nav entity-detail-nav-next" onClick={onNext} aria-label="Next place">
            <CaretRight size={20} />
          </button>
        )}
        {itemIndex !== undefined && totalItems !== undefined && (
          <div className="entity-detail-counter">
            {itemIndex + 1} of {totalItems}
          </div>
        )}

        <div className="entity-detail-scroll" ref={scrollRef}>
          {/* Header */}
          <div className="entity-detail-header">
            <h2 className="entity-detail-name" id={titleId}>{displayName}</h2>
            {place_data?.primary_type && (
              <span className="enrichment-card-badge">{place_data.primary_type}</span>
            )}
          </div>

          {/* Image */}
          {displayImage && (
            <div className="place-detail-image">
              <img src={displayImage} alt={displayName} />
            </div>
          )}

          {/* Contact Info / Place Data */}
          {place_data && (
            <div className="entity-detail-place">
              {place_data.website && (
                <div className="entity-detail-place-row">
                  <Globe size={14} />
                  <a href={place_data.website} target="_blank" rel="noopener noreferrer">
                    {getHostname(place_data.website)}
                  </a>
                </div>
              )}
              {place_data.formatted_address && (
                <div className="entity-detail-place-row">
                  <MapPin size={14} />
                  {mapsUrl ? (
                    <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                      {place_data.formatted_address}
                    </a>
                  ) : (
                    <span>{place_data.formatted_address}</span>
                  )}
                </div>
              )}
              {place_data.phone_number && (
                <div className="entity-detail-place-row">
                  <Phone size={14} />
                  <span>{place_data.phone_number}</span>
                </div>
              )}
              {place_data.rating && (
                <div className="entity-detail-place-row">
                  <span style={{ fontSize: '14px' }}>★</span>
                  <span>{place_data.rating}</span>
                </div>
              )}
            </div>
          )}

          {/* COL-127: Directions/Reviews deep-links, see ExplorationLinks.jsx */}
          <ExplorationLinks links={primaryEnrichment.exploration_links} />

          {/* Description: show Google editorial_summary or AI answer when no creator content */}
          {descriptionText && (
            <div className="entity-detail-answer">
              <p>{descriptionText}</p>
            </div>
          )}

          {/* Creator Notes with attribution */}
          {hasCreatorContent && (
            <div className="place-detail-creator-notes">
              <h3 className="entity-detail-section-title">Creator Notes</h3>
              {creator_contexts.map((ctx, i) => (
                <div key={i} className="place-detail-creator-note">
                  <p className="place-detail-creator-quote">"{ctx.text}"</p>
                  <div className="place-detail-creator-source">
                    {ctx.item_image && (
                      <img src={ctx.item_image} alt="" className="place-detail-creator-thumb" />
                    )}
                    <span className="place-detail-creator-headline">
                      {i === 0 && originalPoster ? (
                        sourceUrl ? (
                          <a href={sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
                            By @{originalPoster}{likelySource ? ` on ${getPlatformName(likelySource)}` : ''}
                          </a>
                        ) : (
                          <>By @{originalPoster}{likelySource ? ` on ${getPlatformName(likelySource)}` : ''}</>
                        )
                      ) : ctx.item_headline ? (
                        <span
                          style={{ color: 'var(--accent)', textDecoration: 'none', cursor: 'pointer' }}
                          onClick={() => onOpenItem?.(ctx.item_id)}
                        >
                          From {ctx.item_headline}
                        </span>
                      ) : null}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <hr className="entity-detail-divider" />

          {/* Referenced Items - click opens item modal instead of navigating */}
          {sources.length > 0 && (
            <div className="entity-detail-parent">
              <h3 className="entity-detail-section-title">
                {sources.length === 1 ? 'From Item' : `Referenced in ${sources.length} Items`}
              </h3>
              {sources.map((src, i) => (
                <div
                  key={i}
                  style={{ cursor: 'pointer' }}
                  onClick={() => onOpenItem?.(src.item_id)}
                >
                  <div className="entity-detail-parent-card" style={i > 0 ? { marginTop: 'var(--space-sm)' } : undefined}>
                    {src.item_image && (
                      <div className="entity-detail-parent-thumb">
                        <img src={src.item_image} alt="" />
                      </div>
                    )}
                    <div className="entity-detail-parent-info">
                      {src.item_headline && <div className="entity-detail-parent-headline">{src.item_headline}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Sibling Entities - click opens place modal for places */}
          {siblings.length > 0 && (
            <div className="entity-detail-siblings">
              <h3 className="entity-detail-section-title">Other Entities from this Item</h3>
              <div className="entity-detail-siblings-grid">
                {siblings.map(s => (
                  <div
                    key={s.enrichment_id}
                    className="entity-detail-sibling-card"
                    style={{ cursor: s.entity_type === 'place' && onOpenPlace ? 'pointer' : 'default' }}
                    onClick={() => s.entity_type === 'place' && onOpenPlace?.(s)}
                  >
                    <span className="enrichment-card-badge" style={{ fontSize: '0.625rem' }}>{s.entity_type}</span>
                    <span className="entity-detail-sibling-name">{s.entity_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add to Collection */}
          {primaryEnrichment.enrichment_id && (
            <div style={{ marginTop: 'var(--space-md)' }}>
              <button
                className="btn btn-ghost btn-sm btn-add-to-collection"
                onClick={() => setShowCollectionPicker(true)}
              >
                <FolderPlusIcon /> Add to collection
              </button>
              {collectionToast && (
                <div style={{ marginTop: 'var(--space-xs)', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  {collectionToast}
                </div>
              )}
            </div>
          )}

          {/* IDs */}
          {primaryEnrichment.enrichment_id && (
            <div style={{ marginTop: 'var(--space-lg)' }}>
              {primaryEnrichment.item_id && (
                <>
                  <h3 className="entity-detail-section-title">Item ID</h3>
                  <CopyableId value={primaryEnrichment.item_id} />
                </>
              )}
              <div style={{ marginTop: primaryEnrichment.item_id ? 'var(--space-sm)' : 0 }}>
                <h3 className="entity-detail-section-title">Enrichment ID</h3>
                <CopyableId value={primaryEnrichment.enrichment_id} />
              </div>
              {sourceItemDetail?.latest_analysis?.trace_id && (
                <div style={{ marginTop: 'var(--space-sm)' }}>
                  <h3 className="entity-detail-section-title">Trace ID</h3>
                  <CopyableId value={sourceItemDetail.latest_analysis.trace_id} />
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      <AnimatePresence>
        {showCollectionPicker && primaryEnrichment.enrichment_id && (
          <CollectionPickerModal
            items={[{ item_id: primaryEnrichment.enrichment_id, item_type: 'enrichment' }]}
            onClose={() => setShowCollectionPicker(false)}
            onSuccess={(msg) => {
              setCollectionToast(msg);
              if (collectionToastRef.current) clearTimeout(collectionToastRef.current);
              collectionToastRef.current = setTimeout(() => setCollectionToast(null), 3000);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function FolderPlusIcon() {
  return <FolderPlus size={14} />;
}
